import React, { useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import type { ChangeLogEntry } from './types';

interface ChangeLogTabProps {
  entries: ChangeLogEntry[];
}

const CATEGORY_CONFIG: Record<string, { icon: string; bg: string; border: string; label: string }> = {
  auto_fixed:     { icon: '\u2705', bg: 'bg-[#D1FAE5]', border: 'border-[#A7F3D0]', label: 'Auto-Fixed' },
  user_decision:  { icon: '\uD83D\uDC64', bg: 'bg-[#EDE9FE]', border: 'border-[#C4B5FD]', label: 'User Decision' },
  flagged:        { icon: '\u26A0\uFE0F', bg: 'bg-[#FEF3C7]', border: 'border-[#FDE68A]', label: 'Flagged' },
};

const FILTER_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'auto_fixed', label: 'Auto-Fixed' },
  { id: 'user_decision', label: 'User Decisions' },
  { id: 'flagged', label: 'Flagged' },
];

const ChangeLogTab: React.FC<ChangeLogTabProps> = ({ entries }) => {
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showDetail, setShowDetail] = useState(false);

  const filtered = filter === 'all' ? entries : entries.filter(e => e.category === filter);

  if (entries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-sm font-semibold text-[#0F172A]">No changes recorded</p>
        <p className="text-xs text-[#94A3B8] mt-1">No transformations were applied to this dataset.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {FILTER_OPTIONS.map(opt => {
            const count = opt.id === 'all' ? entries.length : entries.filter(e => e.category === opt.id).length;
            return (
              <button
                key={opt.id}
                onClick={() => setFilter(opt.id)}
                className={`px-3 py-1.5 rounded-full text-[12px] font-semibold transition-colors ${
                  filter === opt.id
                    ? 'bg-[#0D9488] text-white'
                    : 'bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]'
                }`}
              >
                {opt.label} ({count})
              </button>
            );
          })}
        </div>
        <button
          onClick={() => setShowDetail(!showDetail)}
          className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold border transition-colors ${
            showDetail ? 'border-[#0D9488] text-[#0D9488] bg-[#F0FDFA]' : 'border-[#E2E8F0] text-[#64748B]'
          }`}
        >
          {showDetail ? 'Plain English' : '+ Detail'}
        </button>
      </div>

      <div className="space-y-2">
        {filtered.map(entry => {
          const config = CATEGORY_CONFIG[entry.category] || CATEGORY_CONFIG.flagged;
          const isOpen = expandedId === entry.id;
          return (
            <div key={entry.id} className={`border border-[#E2E8F0] rounded-xl overflow-hidden bg-white ${entry.category === 'user_decision' ? 'border-l-[3px] border-l-[#7C3AED]' : ''}`}>
              <button
                onClick={() => setExpandedId(isOpen ? null : entry.id)}
                className="w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-[#FAFCFF] transition-colors"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm flex-shrink-0 ${config.bg}`}>
                  {config.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-[#0F172A]">{entry.title}</p>
                  <p className="text-[12px] text-[#64748B] mt-0.5 line-clamp-1">{entry.description}</p>
                  <div className="flex gap-1.5 mt-1.5 flex-wrap">
                    <span className="px-1.5 py-0.5 bg-[#F1F5F9] text-[#64748B] text-[10px] font-medium rounded">{entry.phase}</span>
                    <span className="px-1.5 py-0.5 bg-[#EEF2FF] text-[#4F46E5] text-[10px] font-mono rounded">{entry.field}</span>
                    <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${config.bg} ${config.border}`}>{config.label}</span>
                  </div>
                </div>
                <div className="text-right flex-shrink-0 mr-2">
                  <p className="text-[18px] font-bold text-[#0F172A]">{entry.count}</p>
                  <p className="text-[10px] text-[#94A3B8]">records</p>
                </div>
                {isOpen ? <ChevronDown className="w-4 h-4 text-[#94A3B8] flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-[#94A3B8] flex-shrink-0" />}
              </button>

              {isOpen && (
                <div className="border-t border-[#E2E8F0] px-4 py-3 bg-[#FAFCFF]">
                  {entry.description && (
                    <p className="text-[13px] text-[#0F172A] mb-3 leading-relaxed">{entry.description}</p>
                  )}
                  {entry.category === 'auto_fixed' && (
                    <div className="bg-[#F0FDF4] border border-[#A7F3D0] rounded-lg px-3 py-2 mb-3">
                      <p className="text-[12px] text-[#065F46] italic">Original values are preserved in the audit trail. All changes are fully reversible.</p>
                    </div>
                  )}
                  {entry.category === 'user_decision' && (
                    <div className="bg-[#EDE9FE] border border-[#C4B5FD] rounded-lg px-3 py-2 mb-3">
                      <p className="text-[12px] text-[#4C1D95] italic">This change was applied by a stakeholder decision. The original data and decision rationale are stored in the audit trail.</p>
                    </div>
                  )}
                  {entry.details && entry.details.length > 0 ? (
                    <>
                      <div className="border border-[#E2E8F0] rounded-lg overflow-x-auto">
                        <table className="w-full min-w-[640px] text-[12px]">
                          <thead>
                            <tr className="bg-[#F1F5F9]">
                              <th className="px-3 py-2 text-left font-semibold text-[#64748B] text-[10px] uppercase">Row</th>
                              <th className="px-3 py-2 text-left font-semibold text-[#64748B] text-[10px] uppercase">Field</th>
                              <th className="px-3 py-2 text-left font-semibold text-[#64748B] text-[10px] uppercase">Original Value</th>
                              <th className="px-3 py-2 text-left font-semibold text-[#64748B] text-[10px] uppercase">Current Value</th>
                              <th className="px-3 py-2 text-left font-semibold text-[#64748B] text-[10px] uppercase">Rule</th>
                            </tr>
                          </thead>
                          <tbody>
                            {entry.details.slice(0, showDetail ? 50 : 5).map((d, i) => (
                              <tr key={i} className="border-t border-[#F1F5F9]">
                                <td className="px-3 py-1.5 font-mono text-[#64748B]">{d.row}</td>
                                <td className="px-3 py-1.5 font-mono text-[#334155]">{d.field}</td>
                                <td className="px-3 py-1.5 font-mono text-[#94A3B8] line-through">{d.original_value}</td>
                                <td className="px-3 py-1.5 font-mono text-[#047857] bg-[#DCFCE7]">{d.current_value}</td>
                                <td className="px-3 py-1.5 text-[#64748B]">{d.rule}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {entry.details.length > 5 && !showDetail && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowDetail(true); }}
                          className="mt-2 text-[12px] font-semibold text-[#0D9488] hover:text-[#0B7A70]"
                        >
                          Show all {entry.details.length} rows &darr;
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="bg-[#F1F5F9] rounded-lg px-4 py-3">
                      <p className="text-[12px] text-[#64748B]">
                        {entry.count} records were processed. Row-level detail is available in the Data Preview tab.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ChangeLogTab;
